import { useCallback, useMemo } from 'react';
import { useTranslations } from 'js/hooks/i18n';
import { TFormFieldDepositLimit } from 'js/components/common/Form/types';
import { func, money, range, ascending } from 'js/utils/Validation/validationRules';
import { DEPOSIT_LIMITS_PREDEFINED_TYPE } from 'js/hooks/socket/hooks/useGetCasinoDepositLimitsSettings/constants';
import {
    TValidationMethodConfig,
    IValidationMethodConfigMoney,
    IValidationMethodConfigFunc,
    TValidationMethodConfigRange,
    IValidationMethodConfigAscending
} from 'js/utils/Validation/types';

import { useDepositLimitsConfig } from './useDepositLimitsConfig';
import { UNLIMITED_AMOUNT, IMS_UNLIMITED_AMOUNT } from '../constants';

export const useDepositLimitsValidationConfig = (field?: TFormFieldDepositLimit) => {
    const translate = useTranslations('playtech.mobile.system.tooltip.');
    const limitsConfig = useDepositLimitsConfig(field);
    const {
        isDailyEnabled, isWeeklyEnabled, isMonthlyEnabled,
        playerMustSetLimits, allPeriodsRequired, noLimitsAllowed,
        predefinedType, limit: { allowedLimits } = {}
    } = limitsConfig || {};

    const validateLimit = useCallback((
        rawValue: string, formValues: Record<string, string>
    ): string[] | undefined => {
        if (!field) {
            return;
        }

        const { imsFieldDaily, imsFieldWeekly, imsFieldMonthly } = field;
        const values = {
            ...isDailyEnabled && { [imsFieldDaily]: formValues[imsFieldDaily] },
            ...isWeeklyEnabled && { [imsFieldWeekly]: formValues[imsFieldWeekly] },
            ...isMonthlyEnabled && { [imsFieldMonthly]: formValues[imsFieldMonthly] }
        };

        const value = Number(String(rawValue).replace(',', '.'));
        const areAllEmpty = Object.values(values)
            .map(val => Number(String(val).replace(',', '.')))
            .every(val => val === 0);

        if (playerMustSetLimits && allPeriodsRequired && value === 0) {
            return [translate('limits.allRequired')];
        }

        if (playerMustSetLimits && areAllEmpty) {
            return [translate('limits.oneRequired')];
        }

        if (Number.isNaN(value)) {
            return [translate('amount')];
        }
    }, [
        translate, field,
        allPeriodsRequired, playerMustSetLimits,
        isDailyEnabled, isMonthlyEnabled, isWeeklyEnabled
    ]);

    return useMemo<Record<string, TValidationMethodConfig[]>>(() => {
        if (!field || !limitsConfig) {
            return {};
        }

        const formattedAllowedLimits = (allowedLimits || [])
            .filter(limit => limit !== IMS_UNLIMITED_AMOUNT)
            .map(limit => Number(limit));
        const min = Math.min(...formattedAllowedLimits);
        const max = Math.max(...formattedAllowedLimits);
        const isRangeIncluded = predefinedType === DEPOSIT_LIMITS_PREDEFINED_TYPE.ENABLED && formattedAllowedLimits.length;

        const rangeValidation = range({
            min,
            max,
            exceptions: noLimitsAllowed ? [0, UNLIMITED_AMOUNT] : [0],
            error: playerMustSetLimits && !allPeriodsRequired
                ? translate('enabledType.empty', [min, max])
                : translate('enabledType', [min, max])
        }) as TValidationMethodConfigRange;

        const getAscendingValidation = (period: string): IValidationMethodConfigAscending => ascending({
            exceptions: ['0'],
            error: translate(`limits.error.${period}`),
            fields: [
                isDailyEnabled && 'daydepositlimit',
                isWeeklyEnabled && 'weekdepositlimit',
                isMonthlyEnabled && 'monthdepositlimit'
            ].filter(Boolean)
        }) as IValidationMethodConfigAscending;

        return {
            ...isDailyEnabled && {
                daydepositlimit: [
                    money({ isZeroAccepted: true }) as IValidationMethodConfigMoney,
                    func({ fn: validateLimit }) as IValidationMethodConfigFunc,
                    ...isRangeIncluded ? [rangeValidation] : [],
                    getAscendingValidation('daily')
                ]
            },
            ...isWeeklyEnabled && {
                weekdepositlimit: [
                    money({ isZeroAccepted: true }) as IValidationMethodConfigMoney,
                    func({ fn: validateLimit }) as IValidationMethodConfigFunc,
                    ...isRangeIncluded ? [rangeValidation] : [],
                    getAscendingValidation('weekly')
                ]
            },
            ...isMonthlyEnabled && {
                monthdepositlimit: [
                    money({ isZeroAccepted: true }) as IValidationMethodConfigMoney,
                    func({ fn: validateLimit }) as IValidationMethodConfigFunc,
                    ...isRangeIncluded ? [rangeValidation] : [],
                    getAscendingValidation('monthly')
                ]
            }
        };
    }, [
        translate, field, limitsConfig, validateLimit,
        predefinedType, allowedLimits, noLimitsAllowed, allPeriodsRequired,
        isDailyEnabled, isWeeklyEnabled, isMonthlyEnabled, playerMustSetLimits
    ]);
};
